import { type Member, MemberTypes } from "src/types/matchControl/member";

import type { MemberColumn, NumericMemberColumnKey, TextMemberColumnKey } from "./memberColumns";

/**
 * Parses tabular data copied from a spreadsheet into line-up members. Cells are
 * matched to columns by position, following the active preset's columns (the
 * shared source of truth), and the first row is treated as data. Parsing is
 * lenient: bad Type -> `starter`, bad number -> empty, missing columns -> blank,
 * extra columns -> ignored. `summary` reports how many rows were imported and
 * how many had issues.
 */

export type LineUpParseSummary = {
	importedCount: number;
	issueCount: number;
};

export type LineUpParseResult = {
	members: Member[];
	summary: LineUpParseSummary;
};

const detectDelimiter = (text: string): "\t" | "," => {
	// Spreadsheet copy-paste is tab-separated; fall back to comma for CSV-style pastes.
	// Comma mode is a naive split with no quoting, so it assumes no commas inside a
	// field (the tab path — how Sheets/Excel actually paste — is unaffected).
	if (text.includes("\t")) return "\t";
	if (text.includes(",")) return ",";
	return "\t";
};

const parseNumeric = (raw: string): { value?: number; invalid: boolean } => {
	const trimmed = raw.trim();
	if (trimmed === "") return { value: undefined, invalid: false };
	const parsed = Number(trimmed);
	if (!Number.isFinite(parsed)) return { value: undefined, invalid: true };
	return { value: parsed, invalid: false };
};

const parseType = (raw: string): { value: MemberTypes; defaulted: boolean } => {
	const normalized = raw.trim().toLowerCase();
	const match = Object.values(MemberTypes).find((type) => type === normalized);
	if (match) return { value: match, defaulted: false };
	return { value: MemberTypes.STARTER, defaulted: true };
};

export const parseLineUpPaste = (
	rawText: string,
	columns: readonly MemberColumn[],
): LineUpParseResult => {
	const summary: LineUpParseSummary = { importedCount: 0, issueCount: 0 };

	// Guard defensively so a null/undefined/non-string caller can never throw.
	if (typeof rawText !== "string" || rawText.trim() === "") {
		return { members: [], summary };
	}

	const delimiter = detectDelimiter(rawText);

	const rows = rawText
		.split(/\r\n|\r|\n/)
		.map((row) => row.split(delimiter).map((cell) => cell.trim()))
		.filter((cells) => cells.some((cell) => cell !== ""));

	if (rows.length === 0) {
		return { members: [], summary };
	}

	let affectedRows = 0;

	const members: Member[] = rows.map((cells) => {
		const member: Member = { name: "", number: "", type: MemberTypes.STARTER };
		let rowHasIssue = false;

		columns.forEach((column, index) => {
			const value = cells[index];

			if (value === undefined || value === "") {
				if (column.required) rowHasIssue = true;
				if (value === undefined) return;
			}

			if (column.type === "select") {
				const { value: type, defaulted } = parseType(value);
				member.type = type;
				if (defaulted && value !== "") rowHasIssue = true;
			} else if (column.type === "number") {
				const { value: num, invalid } = parseNumeric(value);
				if (invalid) rowHasIssue = true;
				member[column.key as NumericMemberColumnKey] = num;
			} else {
				// number, name, photo URL — stored verbatim
				member[column.key as TextMemberColumnKey] = value;
			}
		});

		if (rowHasIssue) affectedRows += 1;
		return member;
	});

	summary.importedCount = members.length;
	summary.issueCount = affectedRows;

	return { members, summary };
};
